import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  BillingAddress,
  BillingAddressCreated,
  BillingAddressDeleted,
  BillingAddressUpdated,
  ContractOrProductWithPrice,
  CountryState,
  DeleteBillingAddress,
  FetchBillingAddresses,
  LicenseType,
  MARKETPLACE_ERROR_HANDLERS,
  MarketplaceBillingInformationState,
  PricingInformation,
  ProductIdentifier,
  UpdateBillingAddress
} from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import {
  HandleErrorsGlobalRecord,
  InvalidStateError,
  RefreshWindow,
  ShowErrorNotification,
  ShowSuccessNotification,
  convertToHttpParams,
  deepEqual,
  handleAllErrors,
  handleErrors,
  throwAndHandleError,
  throwIfNullish
} from '@platform-frontend/util';
import { MonoTypeOperatorFunction, Observable, combineLatest, forkJoin, of, throwError } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { AppFlowType, FetchFlowData, FlowCanceled, FlowState, GotoFlowStart, PublishFetchedFlowData, SetFlow } from '../../flow';
import {
  MSG_CONNECT_BILLING_ADDRESS_BILLING_INFORMATION_REQUIRED_MISSING,
  MSG_CONNECT_BILLING_ADDRESS_BILLING_INFORMATION_REQUIRED_NOT_SELECTED,
  MSG_CONNECT_BILLING_ADDRESS_COUNTRY_INVALID,
  MSG_CONNECT_BILLING_ADDRESS_DEFAULT,
  MSG_CONNECT_BILLING_ADDRESS_INTERNAL_VAT_CALCULATION_ERROR,
  MSG_CONNECT_BILLING_ADDRESS_INVALID_VAT_ID,
  MSG_CONNECT_BILLING_ADDRESS_NOT_FOUND,
  MSG_CONNECT_BILLING_ADDRESS_SUCCESS,
  MSG_CONNECT_BILLING_ADDRESS_VAT_SERVICE_NOT_REACHABLE,
  MSG_CONNECT_BILLING_ADDRESS_WRONG_PRICING_INFORMATION,
  MSG_SUBMAN_PRICING_COUNTRY_INVALID,
  MSG_SUBMAN_PRICING_DEFAULT,
  MSG_SUBMAN_PRICING_INTERNAL_VAT_CALCULATION_ERROR,
  MSG_SUBMAN_PRICING_INVALID_VAT_ID,
  MSG_SUBMAN_PRICING_VAT_SERVICE_NOT_REACHABLE
} from '../../notification-messages';
import { ProductState } from '../../product';
import { FetchAssets } from '../asset.actions';
import { AssetState } from '../asset.state';
import { BillingCountryWrapper } from './model/billing-country-wrapper';
import { SubscriptionBillingStateModel } from './model/subscription-billing-state-model';
import {
  ConfirmStashedBillingAddress,
  ConnectBillingAddress,
  ResetStashedBillingAddress,
  StashBillingAddressDeletionForConfirmation,
  StashBillingAddressSelectionForConfirmation,
  StashBillingAddressUpdateForConfirmation,
  StashCountrySelectionForConfirmation,
  UnconnectBillingAddress
} from './subscription-billing.actions';

const DEFAULT_STATE: SubscriptionBillingStateModel = {
  connectedBillingAddress: null,
  loadedConnectedBillingAddress: null,
  connectedCountry: null,
  loadedConnectedCountry: null,
  stashedConnectionChange: null,
  stashedConnectionUpdate: null,
  stashedCountry: null,
  stashedPricingInformation: null
};

@State<SubscriptionBillingStateModel>({
  name: 'subscriptionBilling',
  defaults: DEFAULT_STATE
})
@Injectable()
export class SubscriptionBillingState {
  constructor(private http: HttpClient, private store: Store) {}

  private readonly CONNECT_UNCONNECT_ERROR_HANDLERS: HandleErrorsGlobalRecord = {
    WRONG_PRICING_INFORMATION: new ShowErrorNotification(MSG_CONNECT_BILLING_ADDRESS_WRONG_PRICING_INFORMATION),
    INVALID_VAT_ID: new ShowErrorNotification(MSG_CONNECT_BILLING_ADDRESS_INVALID_VAT_ID),
    VAT_SERVICE_NOT_REACHABLE: new ShowErrorNotification(MSG_CONNECT_BILLING_ADDRESS_VAT_SERVICE_NOT_REACHABLE),
    COUNTRY_INVALID: new ShowErrorNotification(MSG_CONNECT_BILLING_ADDRESS_COUNTRY_INVALID),
    INTERNAL_VAT_CALCULATION_ERROR: new ShowErrorNotification(MSG_CONNECT_BILLING_ADDRESS_INTERNAL_VAT_CALCULATION_ERROR),
    BILLING_INFORMATION_REQUIRED_MISSING: new ShowErrorNotification(MSG_CONNECT_BILLING_ADDRESS_BILLING_INFORMATION_REQUIRED_MISSING),
    BILLING_INFORMATION_REQUIRED_NOT_SELECTED: new ShowErrorNotification(MSG_CONNECT_BILLING_ADDRESS_BILLING_INFORMATION_REQUIRED_NOT_SELECTED),
    NOT_FOUND: new RefreshWindow(MSG_CONNECT_BILLING_ADDRESS_NOT_FOUND),
    DEFAULT: new ShowErrorNotification(MSG_CONNECT_BILLING_ADDRESS_DEFAULT)
  };

  billingAddresses$: Observable<BillingAddress[]> = this.store.select(MarketplaceBillingInformationState.billingAddresses);
  countries$: Observable<string[]> = this.store.select(CountryState.countries);
  flowType$: Observable<AppFlowType> = this.store.select(FlowState.flowType);
  productIdentifier$: Observable<ProductIdentifier | null> = this.store.select(ProductState.productIdentifier);
  contractId$: Observable<string | null> = this.store.select(AssetState.contractId);
  licenseType$: Observable<LicenseType | null> = this.store.select(AssetState.licenseType);

  @Selector()
  static connectedBillingAddress(state: SubscriptionBillingStateModel): BillingAddress | null {
    return state.connectedBillingAddress;
  }

  @Selector()
  static connectedCountry(state: SubscriptionBillingStateModel): string | null {
    return state.connectedCountry;
  }

  @Selector()
  static selectedCountry(state: SubscriptionBillingStateModel): string | null {
    return state.stashedCountry ?? state.connectedCountry;
  }

  @Selector([AssetState.pricingInformation])
  static selectedPricingInformation(state: SubscriptionBillingStateModel, pricingInformation: PricingInformation | null): PricingInformation | null {
    return state.stashedPricingInformation ?? pricingInformation;
  }

  @Selector()
  static stashRequiresStorage(state: SubscriptionBillingStateModel): boolean {
    return state.stashedConnectionChange !== null || state.stashedConnectionUpdate !== null;
  }

  @Selector([SubscriptionBillingState.stashRequiresStorage, AssetState.pricingInformation])
  static stashRequiresConfirmation(
    state: SubscriptionBillingStateModel,
    stashRequiresStorage: boolean,
    pricingInformation: PricingInformation | null
  ): boolean {
    const stashedBillingAddressUpdateRequiresConfirmation: boolean =
      state.stashedConnectionChange === null &&
      this.isEditedAddress(state.stashedConnectionUpdate) &&
      this.stashedBillingAddressRequiresConfirmation(state.connectedBillingAddress, state.stashedConnectionUpdate);
    const stashedBillingAddressChangeRequiresConfirmation: boolean =
      state.stashedConnectionChange !== null &&
      (state.stashedConnectionChange === 'UNCONNECT' ||
        this.stashedBillingAddressRequiresConfirmation(state.connectedBillingAddress, state.stashedConnectionChange));

    return (
      stashRequiresStorage &&
      (this.stashedPricingInformationRequiresConfirmation(pricingInformation, state.stashedPricingInformation) ||
        stashedBillingAddressUpdateRequiresConfirmation ||
        stashedBillingAddressChangeRequiresConfirmation)
    );
  }

  private static stashedPricingInformationRequiresConfirmation(
    pricingInformation: PricingInformation | null,
    stashedPricingInformation: PricingInformation | null
  ): boolean {
    return (
      pricingInformation?.currency !== stashedPricingInformation?.currency ||
      pricingInformation?.netValue !== stashedPricingInformation?.netValue ||
      pricingInformation?.total !== stashedPricingInformation?.total
    );
  }

  private static stashedBillingAddressRequiresConfirmation(connectedBillingAddress: BillingAddress | null, stashedBillingAddress: BillingAddress): boolean {
    return (
      connectedBillingAddress === null ||
      (connectedBillingAddress.vatId !== null && connectedBillingAddress.vatId !== stashedBillingAddress.vatId) ||
      (connectedBillingAddress.vatId === null && this.stashedAddressOrNameRequiresConfirmation(connectedBillingAddress, stashedBillingAddress))
    );
  }

  private static stashedAddressOrNameRequiresConfirmation(connectedBillingAddress: BillingAddress, stashedBillingAddress: BillingAddress): boolean {
    const addressChanged: boolean =
      connectedBillingAddress.address !== stashedBillingAddress.address ||
      connectedBillingAddress.address2 !== stashedBillingAddress.address2 ||
      connectedBillingAddress.postalCode !== stashedBillingAddress.postalCode ||
      connectedBillingAddress.city !== stashedBillingAddress.city ||
      connectedBillingAddress.country !== stashedBillingAddress.country;
    const nameFullyChanged: boolean =
      connectedBillingAddress.firstName !== stashedBillingAddress.firstName && connectedBillingAddress.lastName !== stashedBillingAddress.lastName;
    const nameChanged: boolean =
      connectedBillingAddress.firstName !== stashedBillingAddress.firstName || connectedBillingAddress.lastName !== stashedBillingAddress.lastName;

    return (addressChanged && nameChanged) || (!addressChanged && nameFullyChanged);
  }

  @Selector([MarketplaceBillingInformationState.billingAddresses, SubscriptionBillingState.stashRequiresConfirmation])
  static billingAddresses(state: SubscriptionBillingStateModel, billingAddresses: BillingAddress[], stashRequiresConfirmation: boolean): BillingAddress[] {
    const { connectedBillingAddress, stashedConnectionUpdate } = state;
    if (connectedBillingAddress === null || stashedConnectionUpdate === null || !stashRequiresConfirmation) {
      return billingAddresses;
    }

    const connectedIndex = billingAddresses.findIndex(billingAddress => billingAddress.id === connectedBillingAddress.id);
    const replacement = stashedConnectionUpdate === 'DELETE' ? [] : [stashedConnectionUpdate];
    return billingAddresses
      .slice(0, connectedIndex)
      .concat(replacement)
      .concat(billingAddresses.slice(connectedIndex + 1));
  }

  @Selector([SubscriptionBillingState.stashRequiresConfirmation])
  static selectedBillingAddress(state: SubscriptionBillingStateModel, stashRequiresConfirmation: boolean): BillingAddress | null {
    if (!stashRequiresConfirmation) {
      return state.connectedBillingAddress;
    } else if (state.stashedConnectionChange) {
      return SubscriptionBillingState.getStashedBillingAddress(state.stashedConnectionChange);
    } else if (this.isEditedAddress(state.stashedConnectionUpdate)) {
      return state.stashedConnectionUpdate;
    } else {
      return state.connectedBillingAddress;
    }
  }

  private static getStashedBillingAddress(stashedBillingAddressChange: BillingAddress | 'UNCONNECT' | null): BillingAddress | null {
    return stashedBillingAddressChange === 'UNCONNECT' ? null : stashedBillingAddressChange;
  }

  private static isEditedAddress(stashedConnectionUpdate: BillingAddress | 'DELETE' | null): stashedConnectionUpdate is BillingAddress {
    return stashedConnectionUpdate !== null && stashedConnectionUpdate !== 'DELETE';
  }

  private isDeletedAddress(stashedConnectionUpdate: BillingAddress | 'DELETE' | null): stashedConnectionUpdate is 'DELETE' {
    return stashedConnectionUpdate === 'DELETE';
  }

  private isUneditedAddress(stashedConnectionUpdate: BillingAddress | 'DELETE' | null): stashedConnectionUpdate is null {
    return stashedConnectionUpdate === null;
  }

  @Action(FetchFlowData)
  loadConnectedBillingInformation(ctx: StateContext<SubscriptionBillingStateModel>, { flow }: FetchFlowData): Observable<unknown> {
    if (flow.type !== 'SUBSCRIPTION_MANAGEMENT') {
      return of(null);
    }

    const params = convertToHttpParams({ ...flow.startParams.productIdentifier, contractId: flow.startParams.contractId });
    return this.http.get<BillingCountryWrapper | null>(PLATFORM_ENVIRONMENT.apiUrl + '/subscription/billingInformation', { params }).pipe(
      tap(wrapper =>
        ctx.patchState({
          loadedConnectedBillingAddress: wrapper?.billingInformation ?? null,
          loadedConnectedCountry: wrapper?.country ?? null
        })
      )
    );
  }

  @Action(PublishFetchedFlowData)
  publishConnectedBillingInformation(ctx: StateContext<SubscriptionBillingStateModel>): void {
    const { loadedConnectedBillingAddress, loadedConnectedCountry } = ctx.getState();
    ctx.patchState({
      connectedBillingAddress: loadedConnectedBillingAddress,
      loadedConnectedBillingAddress: DEFAULT_STATE.loadedConnectedBillingAddress,
      connectedCountry: loadedConnectedCountry,
      loadedConnectedCountry: DEFAULT_STATE.loadedConnectedCountry
    });
  }

  @Action(SetFlow)
  @Action(RefreshWindow)
  @Action(FlowCanceled)
  reset(ctx: StateContext<SubscriptionBillingStateModel>): void {
    ctx.setState(DEFAULT_STATE);
  }

  @Action(StashBillingAddressSelectionForConfirmation)
  stashSelection(ctx: StateContext<SubscriptionBillingStateModel>, { billingAddressId }: StashBillingAddressSelectionForConfirmation): Observable<unknown> {
    const { connectedBillingAddress, connectedCountry, stashedConnectionUpdate, stashedCountry } = ctx.getState();
    const unconnectedAddressSelected = billingAddressId !== (connectedBillingAddress?.id ?? 'NONE');

    const noAddressSelectedButCountryChanged = billingAddressId === 'NONE' && stashedCountry !== null && stashedCountry !== connectedCountry;

    if (unconnectedAddressSelected) {
      return this.getBillingAddressAndCountryForSelection(ctx, billingAddressId).pipe(
        handleErrors(MARKETPLACE_ERROR_HANDLERS),
        switchMap(([billingAddress, country]) => this.patchForSelection(ctx, billingAddress ?? 'UNCONNECT', billingAddress, country))
      );
    } else if (SubscriptionBillingState.isEditedAddress(stashedConnectionUpdate)) {
      return this.getCountryByCode(stashedConnectionUpdate.country).pipe(
        handleErrors(MARKETPLACE_ERROR_HANDLERS),
        switchMap(country => this.patchForSelection(ctx, null, stashedConnectionUpdate, country))
      );
    } else if (this.isDeletedAddress(stashedConnectionUpdate)) {
      return throwAndHandleError(MARKETPLACE_ERROR_HANDLERS.DEFAULT, new InvalidStateError('Trying to select a billing address marked for deletion.'));
    } else if (this.isUneditedAddress(stashedConnectionUpdate) && noAddressSelectedButCountryChanged) {
      return this.patchForSelection(ctx, 'UNCONNECT', stashedConnectionUpdate, stashedCountry);
    } else {
      return ctx.dispatch(new ResetStashedBillingAddress());
    }
  }

  private getBillingAddressAndCountryForSelection(
    ctx: StateContext<SubscriptionBillingStateModel>,
    billingAddressId: number | 'NONE'
  ): Observable<[BillingAddress | null, string | null]> {
    const selectedCountry = SubscriptionBillingState.selectedCountry(ctx.getState());

    const billingAddress$: Observable<BillingAddress | null> = billingAddressId !== 'NONE' ? this.getBillingAddressById(billingAddressId) : of(null);

    const country$ = billingAddress$.pipe(
      switchMap(billingAddress => (billingAddress !== null ? this.getCountryByCode(billingAddress.country) : of(selectedCountry)))
    );

    return forkJoin([billingAddress$, country$]);
  }

  private patchForSelection(
    ctx: StateContext<SubscriptionBillingStateModel>,
    billingAddressToUpdate: BillingAddress | 'UNCONNECT' | null,
    billingAddressForPricing: BillingAddress | null,
    country: string | null
  ): Observable<unknown> {
    return this.getPricingInformation(billingAddressForPricing, country).pipe(
      tap(pricingInformation =>
        ctx.patchState({
          stashedConnectionChange: billingAddressToUpdate,
          stashedCountry: country,
          stashedPricingInformation: pricingInformation
        })
      )
    );
  }

  private getPricingInformation(billingAddress: BillingAddress | null, country: string | null): Observable<PricingInformation> {
    return this.licenseType$.pipe(
      take(1),
      switchMap(licenseType => this.fetchPricingInformation(licenseType, billingAddress, country))
    );
  }

  private fetchPricingInformation(
    licenseType: LicenseType | null,
    billingAddress: BillingAddress | null,
    country: string | null
  ): Observable<PricingInformation> {
    let applyAction$: Observable<PricingInformation>;

    if (licenseType === null) {
      applyAction$ = throwError(() => new InvalidStateError('Cannot fetch pricing information since no license type is loaded for subscription'));
    } else if (country === null) {
      applyAction$ = throwError(() => new InvalidStateError('Cannot fetch pricing information since no country is loaded for subscription'));
    } else {
      const params = {
        licenseTypeId: licenseType.licenseTypeId,
        userCountryIso2: billingAddress?.country ?? country,
        billingInformationId: billingAddress?.id ?? undefined
      };
      applyAction$ = this.http.post<PricingInformation>(PLATFORM_ENVIRONMENT.apiUrl + '/purchase/priceCalculation', params);
    }

    return applyAction$.pipe(
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        INVALID_VAT_ID: new ShowErrorNotification(MSG_SUBMAN_PRICING_INVALID_VAT_ID),
        VAT_SERVICE_NOT_REACHABLE: new ShowErrorNotification(MSG_SUBMAN_PRICING_VAT_SERVICE_NOT_REACHABLE),
        COUNTRY_INVALID: new ShowErrorNotification(MSG_SUBMAN_PRICING_COUNTRY_INVALID),
        INTERNAL_VAT_CALCULATION_ERROR: new ShowErrorNotification(MSG_SUBMAN_PRICING_INTERNAL_VAT_CALCULATION_ERROR),
        DEFAULT: new ShowErrorNotification(MSG_SUBMAN_PRICING_DEFAULT)
      })
    );
  }

  @Action(StashCountrySelectionForConfirmation)
  stashCountry(ctx: StateContext<SubscriptionBillingStateModel>, { countryCode }: StashCountrySelectionForConfirmation): Observable<unknown> {
    const { connectedCountry, stashedConnectionChange, stashedConnectionUpdate } = ctx.getState();
    if (stashedConnectionChange !== 'UNCONNECT' && stashedConnectionChange !== null) {
      return throwAndHandleError(MARKETPLACE_ERROR_HANDLERS.DEFAULT, new InvalidStateError('Country changes are only allowed on unconnect.'));
    } else if (stashedConnectionUpdate !== null || countryCode !== connectedCountry) {
      return this.getCountryByCode(countryCode).pipe(
        handleErrors(MARKETPLACE_ERROR_HANDLERS),
        switchMap(country => this.patchForSelection(ctx, 'UNCONNECT', null, country))
      );
    } else {
      return ctx.dispatch(new ResetStashedBillingAddress());
    }
  }

  @Action(StashBillingAddressUpdateForConfirmation)
  stashUpdate(ctx: StateContext<SubscriptionBillingStateModel>, { billingAddress }: StashBillingAddressUpdateForConfirmation): Observable<unknown> {
    const { connectedBillingAddress, stashedConnectionChange } = ctx.getState();
    const isDeeplyEqualToConnected = deepEqual(connectedBillingAddress, billingAddress);

    if (connectedBillingAddress?.id !== billingAddress.id) {
      return throwAndHandleError(MARKETPLACE_ERROR_HANDLERS.DEFAULT, new InvalidStateError('Cannot stash an update for an unconnected billing address.'));
    } else if (stashedConnectionChange) {
      ctx.patchState({ stashedConnectionUpdate: isDeeplyEqualToConnected ? null : billingAddress });
      return of(null);
    } else if (!isDeeplyEqualToConnected) {
      return this.getCountryByCode(billingAddress.country).pipe(
        handleErrors(MARKETPLACE_ERROR_HANDLERS),
        switchMap(country =>
          this.getPricingInformation(billingAddress, country).pipe(
            tap(pricingInformation =>
              ctx.patchState({
                stashedConnectionUpdate: billingAddress,
                stashedCountry: country,
                stashedPricingInformation: pricingInformation
              })
            )
          )
        )
      );
    } else {
      return ctx.dispatch(new ResetStashedBillingAddress());
    }
  }

  @Action(StashBillingAddressDeletionForConfirmation)
  stashDeletion(ctx: StateContext<SubscriptionBillingStateModel>): Observable<unknown> {
    const { stashedConnectionChange } = ctx.getState();
    if (stashedConnectionChange) {
      ctx.patchState({ stashedConnectionUpdate: 'DELETE' });
      return of(null);
    } else {
      return this.billingAddresses$.pipe(
        take(1),
        map(billingAddresses => this.getFirstAddressAfterDeletion(ctx, billingAddresses)),
        switchMap(billingAddress => this.getCountryForBillingAddress(ctx, billingAddress).pipe(map(country => ({ billingAddress, country })))),
        handleErrors(MARKETPLACE_ERROR_HANDLERS),
        switchMap(({ billingAddress, country }) =>
          this.getPricingInformation(billingAddress, country).pipe(
            tap(pricingInformation =>
              ctx.patchState({
                stashedConnectionUpdate: 'DELETE',
                stashedConnectionChange: billingAddress ?? 'UNCONNECT',
                stashedCountry: country,
                stashedPricingInformation: pricingInformation
              })
            )
          )
        )
      );
    }
  }

  private getFirstAddressAfterDeletion(ctx: StateContext<SubscriptionBillingStateModel>, billingAddresses: BillingAddress[]): BillingAddress | null {
    const connectedId = ctx.getState().connectedBillingAddress?.id;
    const applicableAddresses = billingAddresses.filter(address => address.id !== connectedId);
    return applicableAddresses[0] ?? null;
  }

  private getCountryForBillingAddress(ctx: StateContext<SubscriptionBillingStateModel>, billingAddress: BillingAddress | null): Observable<string | null> {
    if (billingAddress === null) {
      return of(SubscriptionBillingState.selectedCountry(ctx.getState()));
    } else {
      return this.getCountryByCode(billingAddress.country);
    }
  }

  @Action(BillingAddressCreated)
  autoStashCreatedBillingAddress(ctx: StateContext<SubscriptionBillingStateModel>, { billingAddress }: BillingAddressCreated): Observable<unknown> {
    return this.flowType$.pipe(
      take(1),
      map(flowType => flowType === 'SUBSCRIPTION_MANAGEMENT'),
      switchMap(autoStash => (autoStash ? ctx.dispatch(new StashBillingAddressSelectionForConfirmation(billingAddress.id)) : of(null)))
    );
  }

  @Action(BillingAddressUpdated)
  updateOnUpdate(ctx: StateContext<SubscriptionBillingStateModel>, { original, update }: BillingAddressUpdated): Observable<unknown> {
    const { connectedBillingAddress, stashedConnectionChange } = ctx.getState();
    if (connectedBillingAddress?.id === original.id) {
      return this.getCountryByCode(update.country).pipe(
        tap(country =>
          ctx.patchState({
            connectedBillingAddress: update,
            connectedCountry: country
          })
        ),
        handleErrors(MARKETPLACE_ERROR_HANDLERS)
      );
    } else if (stashedConnectionChange !== 'UNCONNECT' && stashedConnectionChange?.id === original.id) {
      return this.updateStashedConnectionChange(ctx, update);
    } else {
      return of(null);
    }
  }

  @Action(BillingAddressDeleted)
  deleteSelectedBillingAddress(ctx: StateContext<SubscriptionBillingStateModel>, { billingAddress }: BillingAddressDeleted): Observable<unknown> {
    const stashedConnectionChange = ctx.getState().stashedConnectionChange;
    if (stashedConnectionChange !== 'UNCONNECT' && billingAddress.id === stashedConnectionChange?.id) {
      return this.billingAddresses$.pipe(
        take(1),
        map(billingAddresses => this.getFirstAddressAfterDeletion(ctx, billingAddresses)),
        switchMap(newStashedConnectionChange => this.updateStashedConnectionChange(ctx, newStashedConnectionChange))
      );
    } else {
      return of(null);
    }
  }

  private updateStashedConnectionChange(ctx: StateContext<SubscriptionBillingStateModel>, billingAddress: BillingAddress | null): Observable<unknown> {
    return this.getCountryForBillingAddress(ctx, billingAddress).pipe(
      handleErrors(MARKETPLACE_ERROR_HANDLERS),
      switchMap(country => this.patchForSelection(ctx, billingAddress ?? 'UNCONNECT', billingAddress, country))
    );
  }

  @Action(ConfirmStashedBillingAddress)
  applyStash(ctx: StateContext<SubscriptionBillingStateModel>, { gotoFlowStart }: ConfirmStashedBillingAddress): Observable<unknown> {
    const { connectedBillingAddress } = ctx.getState();

    return this.dispatchConnectIfAny(ctx).pipe(
      switchMap(() => this.dispatchUpdateOrDeleteIfAny(ctx, connectedBillingAddress, gotoFlowStart)),
      switchMap(() => ctx.dispatch(new ShowSuccessNotification(MSG_CONNECT_BILLING_ADDRESS_SUCCESS))),
      switchMap(() => this.handleSubscriptionUpdated(ctx, gotoFlowStart))
    );
  }

  private handleSubscriptionUpdated(ctx: StateContext<SubscriptionBillingStateModel>, gotoFlowStart: boolean): Observable<unknown> {
    return ctx
      .dispatch(new ResetStashedBillingAddress())
      .pipe(switchMap(() => (gotoFlowStart ? ctx.dispatch(new GotoFlowStart({ clearNotifications: false })) : of(null))));
  }

  private dispatchConnectIfAny(ctx: StateContext<SubscriptionBillingStateModel>): Observable<unknown> {
    const { stashedConnectionChange, stashedCountry, stashedPricingInformation } = ctx.getState();

    if (stashedConnectionChange === null) {
      return of(null);
    } else if (stashedPricingInformation === null) {
      return throwAndHandleError(MARKETPLACE_ERROR_HANDLERS.DEFAULT, new InvalidStateError('No prices stashed for selection'));
    } else if (stashedConnectionChange !== 'UNCONNECT') {
      return ctx
        .dispatch(new ConnectBillingAddress(stashedConnectionChange.id, stashedPricingInformation))
        .pipe(switchMap(() => ctx.dispatch(new FetchAssets())));
    } else if (stashedCountry === null) {
      return throwAndHandleError(MARKETPLACE_ERROR_HANDLERS.DEFAULT, new InvalidStateError('No country stashed for selection'));
    } else {
      return ctx.dispatch(new UnconnectBillingAddress(stashedCountry, stashedPricingInformation)).pipe(switchMap(() => ctx.dispatch(new FetchAssets())));
    }
  }

  private dispatchUpdateOrDeleteIfAny(
    ctx: StateContext<SubscriptionBillingStateModel>,
    previouslyConnectedBillingAddress: BillingAddress | null,
    gotoFlowStart: boolean
  ): Observable<unknown> {
    const { stashedConnectionChange, stashedConnectionUpdate } = ctx.getState();

    if (stashedConnectionUpdate === null) {
      return of(null);
    } else if (stashedConnectionUpdate === 'DELETE') {
      return this.dispatchDelete(ctx, previouslyConnectedBillingAddress).pipe(this.handleErrorsAfterSubscriptionUpdated(ctx, gotoFlowStart));
    } else if (stashedConnectionChange !== null) {
      return ctx.dispatch(new UpdateBillingAddress(stashedConnectionUpdate)).pipe(this.handleErrorsAfterSubscriptionUpdated(ctx, gotoFlowStart));
    } else {
      return this.dispatchUpdateOfConnectedAddress(ctx, stashedConnectionUpdate);
    }
  }

  private dispatchDelete(ctx: StateContext<SubscriptionBillingStateModel>, previouslyConnectedBillingAddress: BillingAddress | null): Observable<unknown> {
    if (previouslyConnectedBillingAddress === null) {
      return throwAndHandleError(MARKETPLACE_ERROR_HANDLERS.DEFAULT, new InvalidStateError('No billing address has been connected that could be modified.'));
    } else {
      return ctx.dispatch(new DeleteBillingAddress(previouslyConnectedBillingAddress.id));
    }
  }

  private handleErrorsAfterSubscriptionUpdated<T>(ctx: StateContext<SubscriptionBillingStateModel>, gotoFlowStart: boolean): MonoTypeOperatorFunction<T> {
    return obs$ => obs$.pipe(handleAllErrors(() => this.handleSubscriptionUpdated(ctx, gotoFlowStart)));
  }

  private dispatchUpdateOfConnectedAddress(ctx: StateContext<SubscriptionBillingStateModel>, stashedConnectionUpdate: BillingAddress): Observable<unknown> {
    const { stashedPricingInformation } = ctx.getState();

    if (stashedPricingInformation === null) {
      return throwAndHandleError(MARKETPLACE_ERROR_HANDLERS.DEFAULT, new InvalidStateError('No pricing information loaded yet.'));
    } else {
      return this.getOverrideSubscription(stashedPricingInformation).pipe(
        handleErrors(MARKETPLACE_ERROR_HANDLERS),
        switchMap(overrideSubscription => ctx.dispatch(new UpdateBillingAddress(stashedConnectionUpdate, overrideSubscription))),
        switchMap(() => ctx.dispatch(new FetchAssets()))
      );
    }
  }

  @Action(ConnectBillingAddress)
  connectBillingAddress(
    ctx: StateContext<SubscriptionBillingStateModel>,
    { billingAddressId, pricingInformation }: ConnectBillingAddress
  ): Observable<unknown> {
    return this.getOverrideSubscription(pricingInformation).pipe(
      map(overrideSubscription => ({ ...overrideSubscription, billingInformationId: billingAddressId })),
      switchMap(params =>
        this.http.put<void>(PLATFORM_ENVIRONMENT.apiUrl + '/subscription/billingInformation/connect', params).pipe(map(() => params.contractId))
      ),
      switchMap(contractId => forkJoin([ctx.dispatch(new FetchBillingAddresses(contractId)), this.connectBillingAddressLocally(ctx, billingAddressId)])),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, this.CONNECT_UNCONNECT_ERROR_HANDLERS)
    );
  }

  private connectBillingAddressLocally(ctx: StateContext<SubscriptionBillingStateModel>, billingAddressId: number): Observable<unknown> {
    return this.getBillingAddressById(billingAddressId).pipe(
      tap(connectedBillingAddress => ctx.patchState({ connectedBillingAddress })),
      switchMap(billingAddress => this.getCountryByCode(billingAddress.country)),
      tap(connectedCountry => ctx.patchState({ connectedCountry }))
    );
  }

  @Action(UnconnectBillingAddress)
  unconnectBillingAddress(ctx: StateContext<SubscriptionBillingStateModel>, { countryCode, pricingInformation }: UnconnectBillingAddress): Observable<unknown> {
    return this.getOverrideSubscription(pricingInformation).pipe(
      map(overrideSubscription => ({ contractOrProductWithPrice: overrideSubscription, userCountry: countryCode })),
      switchMap(params =>
        this.http
          .put<void>(PLATFORM_ENVIRONMENT.apiUrl + '/subscription/billingInformation/unconnect', params)
          .pipe(map(() => params.contractOrProductWithPrice.contractId))
      ),
      switchMap(contractId => forkJoin([ctx.dispatch(new FetchBillingAddresses(contractId)), this.unconnectBillingAddressLocally(ctx, countryCode)])),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, this.CONNECT_UNCONNECT_ERROR_HANDLERS)
    );
  }

  private unconnectBillingAddressLocally(ctx: StateContext<SubscriptionBillingStateModel>, countryCode: string): Observable<unknown> {
    return this.getCountryByCode(countryCode).pipe(tap(country => ctx.patchState({ connectedBillingAddress: null, connectedCountry: country })));
  }

  private getBillingAddressById(billingAddressId: number): Observable<BillingAddress> {
    return this.billingAddresses$.pipe(
      take(1),
      map(billingAddresses => billingAddresses.find(billingAddress => billingAddress.id === billingAddressId)),
      throwIfNullish(() => new InvalidStateError(`Id ${billingAddressId} does not correspond to a known billing address.`))
    );
  }

  private getCountryByCode(countryCode: string): Observable<string> {
    return this.countries$.pipe(
      take(1),
      map(countries => countries.find(country => country === countryCode)),
      throwIfNullish(() => new InvalidStateError(`Country code ${countryCode} does not correspond to a known country.`))
    );
  }

  private getOverrideSubscription(pricingInformation: PricingInformation): Observable<ContractOrProductWithPrice> {
    return combineLatest([
      this.productIdentifier$.pipe(throwIfNullish(() => new InvalidStateError(`Product Identifier not available`))),
      this.contractId$
    ]).pipe(
      take(1),
      map(([productIdentifier, contractId]) => ({ contractId, productIdentifier, pricingInformation }))
    );
  }

  @Action(ResetStashedBillingAddress)
  resetStash(ctx: StateContext<SubscriptionBillingStateModel>): void {
    ctx.patchState({
      stashedConnectionChange: null,
      stashedCountry: null,
      stashedPricingInformation: null,
      stashedConnectionUpdate: null
    });
  }
}
